import type { LucideIcon } from 'lucide-react'
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import './Button.css'

/**
 * The shared Button, design.md 8.1. ICE adds one variant to the design system's set:
 * `destructive-tertiary`, red text with no fill, for a Delete or Remove sitting among other
 * plain actions (a table row's, say). The filled `destructive` is for confirming.
 *
 * Renders a router <Link> with `to`, an <a> with `href`, and a <button> otherwise.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'destructive-tertiary'
export type ButtonSize = 'small' | 'regular' | 'large'

interface Common {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: LucideIcon
  fullWidth?: boolean
  children?: ReactNode
}

type Props =
  | (Common & { to: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>)
  | (Common & { href: string; to?: never } & AnchorHTMLAttributes<HTMLAnchorElement>)
  | (Common & { href?: never; to?: never } & ButtonHTMLAttributes<HTMLButtonElement>)

/** The classes a Button wears, for the odd element that has to look like one (a file
 *  picker's <label>). */
export function buttonClass({ variant = 'primary', size = 'regular', fullWidth = false }: Pick<Common, 'variant' | 'size' | 'fullWidth'> = {}) {
  return ['btn', `btn-${variant}`, `btn-${size}`, fullWidth ? 'btn-full' : ''].filter(Boolean).join(' ')
}

export function Button(props: Props) {
  const { variant, size, icon: Icon, fullWidth, children, className, ...rest } = props
  const classes = [buttonClass({ variant, size, fullWidth }), className].filter(Boolean).join(' ')
  const content = (
    <>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </>
  )
  if ('to' in rest && rest.to !== undefined) {
    return <Link className={classes} {...(rest as { to: string })}>{content}</Link>
  }
  if ('href' in rest && rest.href !== undefined) {
    return <a className={classes} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>{content}</a>
  }
  const { type = 'button', ...button } = rest as ButtonHTMLAttributes<HTMLButtonElement>
  return <button type={type} className={classes} {...button}>{content}</button>
}
